import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  AlertTriangle, Plus, Search, Filter, RefreshCw, FileText,
  Clock, User, MapPin, CheckCircle, XCircle, Eye, Send
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = [
  { id: "fall", name: "Fall" },
  { id: "medication_error", name: "Medication Error" },
  { id: "skin_integrity", name: "Skin Integrity Issue" },
  { id: "equipment_failure", name: "Equipment Failure" },
  { id: "missed_visit", name: "Missed Visit" },
  { id: "complaint", name: "Patient/Family Complaint" },
  { id: "near_miss", name: "Near Miss" },
  { id: "adverse_event", name: "Adverse Event" },
  { id: "abuse_neglect", name: "Suspected Abuse/Neglect" },
  { id: "hipaa_breach", name: "HIPAA/Privacy Breach" },
  { id: "staff_injury", name: "Staff Injury" },
  { id: "other", name: "Other" }
];

const IncidentReporting = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [showForm, setShowForm] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [newIncident, setNewIncident] = useState({
    incident_type: '',
    severity: 'medium',
    patient_name: '',
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    location: '',
    description: '',
    immediate_actions: '',
    witnesses: '',
    injuries: '',
    anonymous: false
  });
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const fetchIncidents = useCallback(async () => {
    try {
      let url = `${API}/api/phase47/incidents?days=30`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterSeverity) url += `&severity=${filterSeverity}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents || []);
      }
    } catch (err) {
      console.error('Error fetching incidents:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, filterSeverity]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const submitIncident = async () => {
    try {
      const res = await fetch(`${API}/api/phase47/incidents/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newIncident,
          witnesses: newIncident.witnesses.split(',').map(w => w.trim()).filter(Boolean)
        })
      });
      
      if (res.ok) {
        toast({
          title: "Incident Reported",
          description: "Your incident report has been submitted successfully."
        });
        setShowForm(false);
        setNewIncident({
          incident_type: '',
          severity: 'medium',
          patient_name: '',
          incident_date: new Date().toISOString().split('T')[0],
          incident_time: '',
          location: '',
          description: '',
          immediate_actions: '',
          witnesses: '',
          injuries: '',
          anonymous: false
        });
        fetchIncidents();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to submit incident report", variant: "destructive" });
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-600 text-white',
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return styles[severity] || styles.medium;
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.submitted;
  };

  const getIncidentTypeName = (typeId) => {
    return INCIDENT_TYPES.find(t => t.id === typeId)?.name || typeId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="incident-reporting">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="h-7 w-7 text-amber-500" />
            Incident Reporting
          </h1>
          <p className="text-gray-600">Report and track safety incidents</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-red-600 hover:bg-red-700"
          data-testid="report-incident-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Report Incident
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <select 
          className="px-3 py-2 border rounded-md"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select 
          className="px-3 py-2 border rounded-md"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Incidents List */}
      <div className="space-y-4">
        {incidents.length > 0 ? (
          incidents.map((incident) => (
            <Card 
              key={incident.id}
              className={`border-l-4 ${
                incident.severity === 'critical' ? 'border-l-red-600' :
                incident.severity === 'high' ? 'border-l-red-400' :
                incident.severity === 'medium' ? 'border-l-amber-400' : 'border-l-green-400'
              }`}
              data-testid={`incident-${incident.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{getIncidentTypeName(incident.incident_type)}</h3>
                      <Badge className={getSeverityBadge(incident.severity)}>
                        {incident.severity}
                      </Badge>
                      <Badge className={getStatusBadge(incident.status)}>
                        {incident.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{incident.description}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      {incident.patient_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {incident.patient_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {incident.incident_date}
                      </span>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedIncident(incident)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-300 mb-3" />
              <h3 className="font-semibold text-gray-600">No Incidents</h3>
              <p className="text-sm text-gray-500">No incidents match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New Incident Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Report an Incident
              </CardTitle>
              <CardDescription>
                Complete all required fields to submit an incident report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Incident Type *</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={newIncident.incident_type}
                    onChange={(e) => setNewIncident({ ...newIncident, incident_type: e.target.value })}
                  >
                    <option value="">Select type...</option>
                    {INCIDENT_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Severity *</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={newIncident.severity}
                    onChange={(e) => setNewIncident({ ...newIncident, severity: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Patient Name (if applicable)</label>
                  <Input
                    value={newIncident.patient_name}
                    onChange={(e) => setNewIncident({ ...newIncident, patient_name: e.target.value })}
                    placeholder="Patient name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Location</label>
                  <Input
                    value={newIncident.location}
                    onChange={(e) => setNewIncident({ ...newIncident, location: e.target.value })}
                    placeholder="Where did this occur?"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Date *</label>
                  <Input
                    type="date"
                    value={newIncident.incident_date}
                    onChange={(e) => setNewIncident({ ...newIncident, incident_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Time</label>
                  <Input
                    type="time"
                    value={newIncident.incident_time}
                    onChange={(e) => setNewIncident({ ...newIncident, incident_time: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Description *</label>
                <Textarea
                  value={newIncident.description}
                  onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                  placeholder="Describe what happened in detail..."
                  rows={4}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Immediate Actions Taken</label>
                <Textarea
                  value={newIncident.immediate_actions}
                  onChange={(e) => setNewIncident({ ...newIncident, immediate_actions: e.target.value })}
                  placeholder="What actions were taken immediately?"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Witnesses</label>
                  <Input
                    value={newIncident.witnesses}
                    onChange={(e) => setNewIncident({ ...newIncident, witnesses: e.target.value })}
                    placeholder="Names, separated by commas"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Injuries (if any)</label>
                  <Input
                    value={newIncident.injuries}
                    onChange={(e) => setNewIncident({ ...newIncident, injuries: e.target.value })}
                    placeholder="Describe any injuries"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={newIncident.anonymous}
                  onChange={(e) => setNewIncident({ ...newIncident, anonymous: e.target.checked })}
                />
                <label htmlFor="anonymous" className="text-sm">Submit anonymously</label>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={submitIncident}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={!newIncident.incident_type || !newIncident.description}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Report
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{getIncidentTypeName(selectedIncident.incident_type)}</CardTitle>
                <div className="flex gap-2">
                  <Badge className={getSeverityBadge(selectedIncident.severity)}>
                    {selectedIncident.severity}
                  </Badge>
                  <Badge className={getStatusBadge(selectedIncident.status)}>
                    {selectedIncident.status?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Patient:</span>
                  <p className="font-medium">{selectedIncident.patient_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Date/Time:</span>
                  <p className="font-medium">{selectedIncident.incident_date} {selectedIncident.incident_time}</p>
                </div>
                <div>
                  <span className="text-gray-500">Location:</span>
                  <p className="font-medium">{selectedIncident.location || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Reported:</span>
                  <p className="font-medium">{new Date(selectedIncident.reported_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div>
                <span className="text-gray-500 text-sm">Description:</span>
                <p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedIncident.description}</p>
              </div>
              
              {selectedIncident.immediate_actions && (
                <div>
                  <span className="text-gray-500 text-sm">Immediate Actions:</span>
                  <p className="mt-1 p-3 bg-blue-50 rounded-lg">{selectedIncident.immediate_actions}</p>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelectedIncident(null)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default IncidentReporting;
